import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";

// Material
import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";

// Actions
import { toggleSnack } from "src/state/snackbar/actions";
import { parseFetchOptions } from "src/state/user/actions";

// Config
import colors from "src/config/colors";
import styles from "src/config/styles";
import useTranslate from "src/hooks/useTranslate";
import { getStatementUrl } from "src/config/apiConfig";
import { snackbarTypes } from "src/config/snackbar";

// Hooks
import { useAppInsights } from "src/utils/telemetry/AppInsights";

// Utils
import statements from "src/utils/statements";

// Components
import Button from "src/components/Button";
import Disclaimer from "src/components/Disclaimer";
import LoadingSection from "src/components/LoadingSection";
import Text from "src/components/Text";
import FormSectionHeader from "./FormSectionHeader";
import MarkdownRenderer from "./MarkdownRenderer";

const useStyles = makeStyles(theme => ({
  disclaimer: {
    backgroundColor: "#E5F8FF",
    padding: theme.spacing(2, 3)
  },
  disclaimerText: {
    color: colors.riverBed
  },
  content: {
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2)
    }
  },
  checkboxWrapper: {
    borderBottom: styles.values.border,
    borderTop: styles.values.border,
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2, 1)
    }
  },
  checkboxLabel: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  checkboxText: {
    lineHeight: 1.5
  },
  boxButton: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(3),
    padding: theme.spacing(2, 3)
  }
}));

const FormReview = ({
  onSubmit,
  onGoBack,
  schema,
  values,
  isSai,
  pages,
  pageIndex,
  maxPageIndex,
  onNavigateToPage
}) => {
  const getText = useTranslate();
  const classes = useStyles();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { caseId, itemId } = useParams();
  const { trackException } = useAppInsights();
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();

  const handleClickCheck = () => {
    setTermsAccepted(!termsAccepted);
  };

  const saveStatement = () => {
    setSaving(true);

    dispatch(
      parseFetchOptions(async ({ headers: { Authorization, ...headers } }) => {
        const url = getStatementUrl(caseId, itemId);
        const options = {
          method: "PUT",
          headers: {
            ...headers,
            Authorization,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            responses: statements.formatDateFields(schema, values)
          })
        };

        try {
          setSaving(true);
          const res = await fetch(url, options);
          if (!res.ok) {
            throw new Error(`Request failed with status ${res.status}`);
          }
          onSubmit();
        } catch (err) {
          trackException(
            `Failed to submit statement ${itemId} to case ${caseId}, ${err}`
          );
          dispatch(
            toggleSnack({
              type: snackbarTypes.ERROR,
              text: "common.errors.request"
            })
          );
        } finally {
          setSaving(false);
        }
      })
    );
  };

  if (saving) {
    return <LoadingSection noBackground normalHeight />;
  }

  return (
    <>
      <FormSectionHeader
        isSai={isSai}
        titleKey="statementPage.reviewTitle"
        pages={pages}
        pageIndex={pageIndex}
        maxPageIndex={maxPageIndex}
        onNavigate={onNavigateToPage}
        extraStepLabel={getText("statementPage.reviewStep")}
      />
      <Disclaimer
        className={classes.disclaimer}
        textClassName={classes.disclaimerText}
        text="statementPage.disclaimer"
        dark
      />
      <Box className={classes.content}>
        <MarkdownRenderer schema={schema} values={values} />
      </Box>
      <Box className={classes.checkboxWrapper}>
        <Checkbox
          color="primary"
          disableRipple
          checked={termsAccepted}
          onChange={handleClickCheck}
        />
        <Box className={classes.checkboxLabel}>
          <Text
            type="contentLabel3"
            className={classes.checkboxText}
            text="statementPage.acceptTerms"
          />
        </Box>
      </Box>
      <Box className={classes.boxButton}>
        <Button onClick={onGoBack}>
          {getText("common.prompts.edit").toUpperCase()}
        </Button>
        <Button
          onClick={saveStatement}
          variant="contained"
          disableElevation
          color="primary"
          disabled={!termsAccepted}
        >
          {getText("common.prompts.saveAndSubmit").toUpperCase()}
        </Button>
      </Box>
    </>
  );
};

FormReview.propTypes = {
  isSai: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  schema: PropTypes.shape({}).isRequired,
  onGoBack: PropTypes.func.isRequired,
  values: PropTypes.shape({}).isRequired,
  onNavigateToPage: PropTypes.func.isRequired,
  pageIndex: PropTypes.number.isRequired,
  maxPageIndex: PropTypes.number.isRequired,
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string
    })
  ).isRequired
};

export default FormReview;
